import { Badge, Box, Button, Divider, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useMemo, useReducer, useRef, useState } from "react";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import { isLocalDev } from "../../../../utils/environment";
import { ValidDataService } from "../../../../service/validDataService";
import useAbortController from "../../../../hooks/useAbortController";
import useDialogModal from "../../../../hooks/useDialogModal";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import validLookup from "../../../../utils/validLookup";
import ValidFrameInputForm from "./validFrameInputForm";
import ValidFrameEditForm from "./validFrameEditForm";
import PrintLabelsDialog from "./PrintLabelsDialog";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useValidInventoryColumns } from "../useValidInventoryColumns";
import InventoryFrameImportDialog from "./InventoryFrameImportDialog";
import InventoryLocationActionDialog from "../InventoryLocationActionDialog";
import { DEFAULT_PAGE_SIZE } from "utils/index";
//import EditValidFrameForm from "./editValidFrameForm";

const FRAME_VIEW = {
    inventory: 'inventory',
    sold: 'sold',
    all: 'all'
};

const parseFilter = (filter) => {
    if (!filter) return null;
    if (typeof filter === 'string') {
        try {
            return JSON.parse(filter);
        } catch {
            return null;
        }
    }
    return filter;
};

const buildFrameViewFilters = (filters = [], frameView = FRAME_VIEW.inventory) => {
    const existingFilters = Array.isArray(filters) ? filters : [];

    // Remove any existing quantity filter so view-level filter is deterministic.
    const cleaned = existingFilters.filter((filter) => {
        const parsed = parseFilter(filter);
        return parsed?.id !== 'quantityOnHand' && parsed?.id !== 'inStock';
    });

    if (frameView === FRAME_VIEW.all) {
        return cleaned.map((filter) => (typeof filter === 'string' ? filter : JSON.stringify(filter)));
    }

    const stockFilter = frameView === FRAME_VIEW.sold
        ? { id: 'quantityOnHand', value: { op: 'eq', value: 0 } }
        : { id: 'quantityOnHand', value: { op: 'gt', value: 0 } };

    return [...cleaned, stockFilter].map((filter) => (typeof filter === 'string' ? filter : JSON.stringify(filter)));
};

export default function Frames(props) {
    const navigate = useNavigate();
    const [ValidFrameInputDialog, showValidFrameInputForm] = useDialogModal(ValidFrameInputForm);
    const [ValidFrameEditDialog, showValidFrameEditForm] = useDialogModal(ValidFrameEditForm);
    const [LabelsDialog, showLabelsDialog] = useDialogModal(PrintLabelsDialog);
    const [InventoryImportDialog, showInventoryImportDialog] = useDialogModal(InventoryFrameImportDialog);
    const [InventoryLocationActionDialogComp, showInventoryLocationActionDialog] = useDialogModal(InventoryLocationActionDialog);
    const columns = useValidInventoryColumns('frame');

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const [signal] = useAbortController();
    const lastQueryRef = useRef({ pageSize: DEFAULT_PAGE_SIZE, pageIndex: 0, filters: [], sortBy: [] });
    const [frameView, setFrameView] = useState(FRAME_VIEW.all);
    const [tabCounts, setTabCounts] = useState({
        inventory: null,
        sold: null
    });
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    const onAddRecordClicked = () => {
        showValidFrameInputForm();
    }

    const handleRowEdit = useCallback((rowData) => {
        showValidFrameEditForm(null, { recordToEdit: rowData });
    }, [showValidFrameEditForm]);

    const handleReplenish = useCallback((rowData) => {
        const params = new URLSearchParams({
            subtab: 'procurement',
            procTab: '1',
            prefillInventoryItemId: String(rowData?.id || ''),
            prefillSupplierId: String(rowData?.supplier?.id || ''),
            prefillLocationId: String(rowData?.location?.id || ''),
            prefillModelNumber: String(rowData?.modelNumber || ''),
            prefillUnitCost: rowData?.supplierCost === null || rowData?.supplierCost === undefined
                ? ''
                : String(rowData.supplierCost),
            prefillLandingCost: rowData?.landingCost === null || rowData?.landingCost === undefined
                ? ''
                : String(rowData.landingCost)
        });

        navigate(`/inventory?${params.toString()}`);
    }, [navigate]);

    /**
    * fetch data
    */
    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        lastQueryRef.current = { pageSize, pageIndex, filters, sortBy };
        const from = pageSize * pageIndex;
        const viewFilters = buildFrameViewFilters(filters, frameView);
        updateState({ loading: true });
        ValidDataService.getInventoryDataWithLimit(from, pageSize, viewFilters, sortBy, 'frame', signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });

                if (frameView === FRAME_VIEW.inventory || frameView === FRAME_VIEW.sold) {
                    setTabCounts((prev) => ({
                        ...prev,
                        [frameView]: Number(data.count || 0)
                    }));
                }
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [frameView, signal]);

    const refreshFrames = useCallback(() => {
        const { pageSize, pageIndex, filters, sortBy } = lastQueryRef.current;
        fetchData({ pageSize, pageIndex, filters, sortBy });
    }, [fetchData]);

    const handleInventoryLocationAction = useCallback((operation, selectedRows, clearSelection) => {
        showInventoryLocationActionDialog(async ({ destinationLocationId, destinationLocation }) => {
            try {
                const itemIds = selectedRows.map((row) => row.id);
                const response = operation === 'copy'
                    ? await ValidDataService.copyInventoryItems({ itemIds, destinationLocationId })
                    : await ValidDataService.transferInventoryItems({ itemIds, destinationLocationId });

                const { code, data, message } = response;

                if (code !== 0) {
                    toast.error(message || 'Inventory location action failed');
                    return;
                }

                const summary = data || {};
                const completedCount = operation === 'copy'
                    ? summary.copiedCount || 0
                    : summary.transferredCount || 0;
                const skippedCount = summary.skippedCount || 0;

                toast.success(
                    `${completedCount} item${completedCount === 1 ? '' : 's'} ${operation === 'copy' ? 'copied' : 'transferred'} to ${destinationLocation.name}${skippedCount ? `, ${skippedCount} skipped` : ''}`
                );

                refreshFrames();
                clearSelection && clearSelection();
            } catch (error) {
                if (isLocalDev) {
                    console.error(error);
                }
                toast.error(`Failed to ${operation} selected items`);
            }
        }, {
            operation,
            rows: selectedRows
        });
    }, [refreshFrames, showInventoryLocationActionDialog]);

    const deleteRecordHandler = (rowids, tableState = {}) => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const parsedRowIds = rowids.map((rowid) => parseInt(rowid, 10));
                const deleteRecordResult = await ValidDataService.deleteInventoryRecord(parsedRowIds);
                const { code, message } = deleteRecordResult;

                if (code === 0) {
                    toast.success(message);

                    const currentPageIndex = Number(tableState.pageIndex || 0);
                    const currentPageSize = Number(tableState.pageSize || 10);
                    const nextTotalRowCount = Math.max(0, state.totalRowCount - parsedRowIds.length);
                    const maxPageIndex = Math.max(0, Math.ceil(nextTotalRowCount / currentPageSize) - 1);

                    if (currentPageIndex > maxPageIndex && tableState.gotoPage) {
                        tableState.gotoPage(maxPageIndex);
                    } else if (tableState.refreshCurrentPage) {
                        tableState.refreshCurrentPage();
                    } else {
                        refreshFrames();
                    }
                } else {
                    toast.error("Failed to delete record(s)");
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Please confirm if you would like to delete ${rowids.length} record(s)?`,
        });
    }

    const recordAddHandler = (newRecord) => {
        const newData = state.data.concat(newRecord);
        updateState({ data: newData });
    }

        // Helper: print labels for selected frames using a hidden iframe (no new tab)
    const printFrameLabels = useCallback((rows, options = {}) => {
                if (!rows || rows.length === 0) return;
        const { template = '5160', startPosition = 1, includePrice = true, includeBarcode = true, quantityPerItem = 1 } = options;
                const cfg = template === '5167' ? { columns: 4, perSheet: 80 } : { columns: 3, perSheet: 30 };

                const padCount = Math.max(0, Math.min(cfg.perSheet - 1, parseInt(startPosition, 10) - 1 || 0));
                const placeholders = new Array(padCount).fill(null);
        const q = Math.max(1, parseInt(quantityPerItem, 10) || 1);
        const expandedRows = rows.flatMap(r => Array.from({ length: q }, () => r));

                const html = `<!DOCTYPE html>
                <html>
                    <head>
                        <meta charset="utf-8" />
                        <title>Frame Labels</title>
                        <style>
                            @page { margin: 10mm; }
                            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 12px; }
                            .grid { display: grid; grid-template-columns: repeat(${cfg.columns}, 1fr); gap: 8px; }
                            .label { border: 1px solid #ccc; padding: 8px; border-radius: 6px; }
                            .model { font-weight: 600; font-size: 12px; }
                            .sku { font-size: 11px; color: #444; }
                            .price { font-size: 12px; margin-top: 4px; }
                            @media print { .label { page-break-inside: avoid; } }
                            .barcode { width: 100%; height: 28px; }
                        </style>
                        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
                    </head>
                    <body>
                        <div class="grid">
                            ${[...placeholders, ...expandedRows].map(r => {
                                if (!r) {
                                    return `<div class="label"></div>`;
                                }
                                const model = r.modelNumber || r.model || '';
                                const sku = r.sku || r.internalUpc || '';
                                const price = r.listPrice || r.price || '';
                                return `<div class="label">
                                    <div class="model">${model}</div>
                                    <div class="sku">SKU: ${sku}</div>
                                    ${includePrice ? `<div class="price">${price ? `$${price}` : ''}</div>` : ''}
                                    ${includeBarcode && sku ? `<svg class="barcode" jsbarcode-format="CODE128" jsbarcode-value="${sku}" jsbarcode-textmargin="0" jsbarcode-fontoptions="bold"></svg>` : ''}
                                </div>`;
                            }).join('')}
                        </div>
                        <script>
                          try {
                            if (window.JsBarcode) {
                              JsBarcode('.barcode').init();
                            }
                          } catch (e) { console.error('Barcode init failed', e); }
                        </script>
                    </body>
                </html>`;

                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.right = '0';
                iframe.style.bottom = '0';
                iframe.style.width = '0';
                iframe.style.height = '0';
                iframe.style.border = '0';
                document.body.appendChild(iframe);

                const doc = iframe.contentWindow || iframe.contentDocument;
                const iDoc = doc.document || doc;
                iDoc.open();
                iDoc.write(html);
                iDoc.close();

                setTimeout(() => {
                    try {
                        iframe.contentWindow.focus();
                        iframe.contentWindow.print();
                    } catch (e) {
                        console.error('Print failed:', e);
                    } finally {
                        setTimeout(() => {
                            try { document.body.removeChild(iframe); } catch(_) {}
                        }, 1000);
                    }
                }, 200);
        }, []);

    const selectedToolbarActions = useMemo(() => ([
        {
            id: 'transfer-location',
            label: 'Transfer',
            icon: <SwapHorizIcon />,
            tooltip: 'Transfer selected frames to another location',
            variant: 'button',
            onClick: (selectedRows, { clearSelection }) => {
                handleInventoryLocationAction('transfer', selectedRows, clearSelection);
            }
        },
        {
            id: 'copy-location',
            label: 'Copy To Location',
            icon: <ContentCopyIcon />,
            tooltip: 'Copy selected frames to another location',
            variant: 'button',
            onClick: (selectedRows, { clearSelection }) => {
                handleInventoryLocationAction('copy', selectedRows, clearSelection);
            }
        },
        {
            id: 'print-labels',
            label: 'Print Labels',
            icon: <PrintIcon />,
            tooltip: 'Print labels for selected frames',
            variant: 'button',
            onClick: (selectedRows, { clearSelection }) => {
                showLabelsDialog((opts) => {
                    printFrameLabels(selectedRows, opts || {});
                    clearSelection && clearSelection();
                }, { rows: selectedRows });
            }
        }
    ]), [handleInventoryLocationAction, printFrameLabels, showLabelsDialog]);

    const rowActions = useMemo(() => {
        const actions = [
            {
                icon: <EditIcon />,
                onClick: handleRowEdit
            }
        ];

        if (frameView === FRAME_VIEW.sold) {
            actions.push({
                icon: <AddShoppingCartIcon />,
                onClick: handleReplenish,
                tooltip: 'Replenish'
            });
        }
        
        return actions;
    }, [frameView, handleReplenish, handleRowEdit]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4">Frames</Typography>
                <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => showInventoryImportDialog()}>
                    Import Inventory
                </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={frameView}
                    onChange={(_, newValue) => setFrameView(newValue)}
                    aria-label="Frame inventory views"
                >
                    <Tab value={FRAME_VIEW.all} label="All" data-testid="frames-view-all" />
                    <Tab
                        value={FRAME_VIEW.inventory}
                        label={(
                            <Badge
                                color="primary"
                                badgeContent={tabCounts.inventory ?? 0}
                                invisible={tabCounts.inventory === null}
                            >
                                Inventory
                            </Badge>
                        )}
                        data-testid="frames-view-inventory"
                    />
                    <Tab
                        value={FRAME_VIEW.sold}
                        label={(
                            <Badge
                                color="secondary"
                                badgeContent={tabCounts.sold ?? 0}
                                invisible={tabCounts.sold === null}
                            >
                                Sold
                            </Badge>
                        )}
                        data-testid="frames-view-sold"
                    />
                </Tabs>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {frameView === FRAME_VIEW.inventory && 'Inventory shows only frames physically in stock (On Hand > 0).'}
                {frameView === FRAME_VIEW.sold && 'Sold shows out-of-stock frames (On Hand = 0). Use Replenish to create a supplier order.'}
                {frameView === FRAME_VIEW.all && 'All shows every frame regardless of stock level.'}
            </Typography>
            <EnhancedTable
                key={`frames-table-${frameView}`}
                columns={columns}
                addRecordHandler={onAddRecordClicked}
                deleteRecordHandler={deleteRecordHandler}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="multi"
                selectedToolbarActions={selectedToolbarActions}
                actions={rowActions}
            />
            <ValidFrameInputDialog onRecordAdded={refreshFrames}/>
            <ValidFrameEditDialog onRecordUpdated={refreshFrames} onRecordCopied={recordAddHandler} />
            <InventoryImportDialog onImported={refreshFrames} />
            <InventoryLocationActionDialogComp />
            <LabelsDialog />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    )
}